import React, { useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import { LiquidGlass } from '@liquidglass/react'
import { ServiceType } from '../../types'
import { Product } from './ProductGrid'

interface DivisionShowcaseProps {
  className?: string
  services: any[]
  onServiceClick: (serviceType: ServiceType) => void
  isLoading: boolean
  apiConnected: boolean
  activeDivision?: 'software' | 'hardware' | 'services'
  onDivisionChange?: (division: 'software' | 'hardware' | 'services') => void
  activeProduct?: string | null
}

interface Division {
  id: 'software' | 'hardware' | 'services'
  title: string
  tagline: string
  icon: string
  gradient: string
}

const divisions: Division[] = [
  {
    id: 'services',
    title: 'Services',
    tagline: 'Professional Business Solutions',
    icon: '🎯',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
  {
    id: 'software',
    title: 'Software',
    tagline: 'AI, Dev Tools, Creative Software',
    icon: '💻',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  {
    id: 'hardware',
    title: 'Hardware',
    tagline: 'Innovative Physical Products',
    icon: '⚙️',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  }
]

const softwareProducts: Product[] = [
  { 
    id: 'codered', 
    name: 'Project Crimson', 
    description: 'Advanced development framework with intelligent automation', 
    icon: '💻',
    route: '/codered',
    status: 'coming-soon'
  },
  { 
    id: 'beyondreal', 
    name: 'Project Nexus', 
    description: 'Immersive platform for advanced visualization systems', 
    icon: '🎨',
    route: '/beyondreal',
    status: 'coming-soon'
  },
  { 
    id: 'macx-launcher', 
    name: 'Project Velocity', 
    description: 'High-performance system optimization toolkit', 
    icon: '🚀',
    route: '/macx',
    status: 'coming-soon'
  }
]

const hardwareProducts: Product[] = [
  { 
    id: 'macx-handheld', 
    name: 'Project Quantum', 
    description: 'Next-generation portable computing platform', 
    icon: '📱',
    route: '/macx-handheld',
    status: 'coming-soon'
  },
  { 
    id: 'melanin-devices', 
    name: 'Project Resonance', 
    description: 'Biometric integration technology for enhanced experiences', 
    icon: '🔬',
    route: '/melanin-devices',
    status: 'coming-soon'
  }
]

export const DivisionShowcase: React.FC<DivisionShowcaseProps> = ({ 
  className = '', 
  services,
  onServiceClick,
  activeDivision = 'services',
  onDivisionChange,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { once: false, margin: '-100px' })
  const [activeTab, setActiveTab] = useState<'software' | 'hardware' | 'services'>(activeDivision)

  // Update active tab when activeDivision prop changes (from routing)
  React.useEffect(() => {
    setActiveTab(activeDivision)
  }, [activeDivision])

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' 
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
      : false
  )

  const activeGradient = divisions.find(d => d.id === activeTab)?.gradient || divisions[2].gradient

  return (
    <section
      ref={sectionRef}
      id="services"
      className={`relative z-30 py-24 ${className}`}
      style={{ 
        backgroundColor: '#F7F5F5',
        overflow: 'hidden',
        overflowX: 'hidden',
        maxWidth: '100vw',
        width: '100%',
      }}
    >
      {/* Animated background gradient orbs */}
      <motion.div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ 
          background: activeGradient,
          willChange: prefersReducedMotion.current ? 'auto' : 'transform'
        }}
        animate={
          prefersReducedMotion.current
            ? {}
            : {
                scale: [1, 1.2, 1],
                x: [-20, 20, -20],
              }
        }
        transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="max-w-7xl mx-auto relative" style={{ padding: '0 1rem', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          style={{ width: '100%', maxWidth: '100%' }}
          initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 
            className="mb-4 font-bold"
            style={{ 
              color: '#1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 700,
              letterSpacing: '-0.03em',
              fontSize: 'clamp(2rem, 8vw, 3.5rem)',
              lineHeight: '1.2',
              wordBreak: 'break-word',
            }}
          >
            Our Ecosystem
          </h2>
          <p 
            className="max-w-3xl mx-auto"
            style={{ 
              color: '#666666',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 500,
              lineHeight: '1.7',
              fontSize: 'clamp(1rem, 4vw, 1.25rem)',
              wordBreak: 'break-word',
            }}
          >
            Choose from our comprehensive suite of services designed to streamline operations and accelerate growth
          </p>
        </motion.div>

        {/* Liquid Glass Tab Navigation - Responsive */}
        <motion.div 
          className="flex justify-center mb-16"
          style={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}
          initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <div style={{ display: 'inline-flex', maxWidth: 'calc(100vw - 2rem)', width: 'auto' }}>
            <LiquidGlass
              borderRadius={50}
              blur={0.8}
              contrast={1.3}
              brightness={1.15}
              saturation={1.3}
              shadowIntensity={0.3}
              elasticity={0.7}
            >
              <div 
                className="inline-flex gap-1 p-2"
                style={{
                  borderRadius: '50px',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  maxWidth: '100%',
                }}
              >
                {divisions.map((division) => (
                  <motion.button
                    key={division.id}
                    onClick={() => {
                      setActiveTab(division.id)
                      onDivisionChange?.(division.id)
                    }}
                    className="relative rounded-full"
                    style={{
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 600,
                      fontSize: 'clamp(14px, 3vw, 15px)',
                      backgroundColor: activeTab === division.id ? 'rgba(255, 255, 255, 0.3)' : 'transparent',
                      transition: 'background-color 0.3s ease',
                      whiteSpace: 'nowrap',
                      padding: 'clamp(10px, 2vw, 14px) clamp(20px, 4vw, 32px)',
                      minHeight: '44px',
                      minWidth: '44px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {activeTab === division.id && (
                      <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.2) 100%)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        }}
                        layoutId="activeTab"
                        initial={false}
                        transition={{ 
                          type: 'spring', 
                          stiffness: 500, 
                          damping: 35,
                          mass: 0.8,
                        }}
                      />
                    )}
                    <span 
                      className="relative z-10"
                      style={{ 
                        color: activeTab === division.id ? '#1A1A1A' : '#888888',
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {division.title}
                    </span>
                  </motion.button>
                ))}
              </div>
            </LiquidGlass>
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          {/* Software Tab - Floating Glass Names */}
          {activeTab === 'software' && (
            <div className="max-w-4xl mx-auto space-y-8">
              {softwareProducts.map((product, index) => (
                <motion.div
                  key={product.name}
                  className="group relative"
                  initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={
                    prefersReducedMotion.current
                      ? {}
                      : {
                          y: -6,
                          transition: { duration: 0.4, ease: [0.4, 0.0, 0.2, 1] }
                        }
                  }
                >
                  {/* Liquid Glass Name Pill - Blurred */}
                  <motion.div
                    className="inline-block mb-4"
                    style={{
                      filter: 'blur(3px)',
                      userSelect: 'none',
                      pointerEvents: 'none',
                      opacity: 0.5
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 0.5 } : { opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 0.5, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 + 0.2 }}
                  >
                    <LiquidGlass
                      borderRadius={30}
                      blur={0.6}
                      contrast={1.2}
                      brightness={1.1}
                      saturation={1.2}
                      shadowIntensity={0.25}
                      elasticity={0.6}
                    >
                      <div className="px-8 py-4">
                        <h3 
                          className="text-xl font-semibold"
                          style={{ 
                            color: '#1A1A1A',
                            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                            letterSpacing: '-0.01em'
                          }}
                        >
                          {product.name}
                        </h3>
                      </div>
                    </LiquidGlass>
                  </motion.div>

                  {/* Description - Centered */}
                  <motion.p 
                    className="text-base text-center"
                    style={{ 
                      color: '#666666',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 500,
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                  >
                    {product.description}
                  </motion.p>

                  {/* Status Badge - Absolute Top Right */}
                  <motion.span 
                    className="absolute top-4 right-0 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap"
                    style={{ 
                      background: 'rgba(102, 126, 234, 0.1)',
                      color: '#667eea',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.1 + 0.4 }}
                    whileHover={{
                      background: divisions[0].gradient,
                      color: '#FFFFFF',
                      scale: 1.05,
                    }}
                  >
                    Coming Soon
                  </motion.span>

                  {/* Subtle divider */}
                  {index < softwareProducts.length - 1 && (
                    <motion.div
                      className="mt-8 h-px"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.06), transparent)',
                      }}
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    />
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {/* Hardware Tab - Floating Glass Names */}
          {activeTab === 'hardware' && (
            <div className="max-w-4xl mx-auto space-y-8">
              {hardwareProducts.map((product, index) => (
                <motion.div
                  key={product.name}
                  className="group relative"
                  initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={
                    prefersReducedMotion.current
                      ? {}
                      : {
                          y: -6,
                          transition: { duration: 0.4, ease: [0.4, 0.0, 0.2, 1] }
                        }
                  }
                >
                  {/* Liquid Glass Name Pill - Blurred */}
                  <motion.div
                    className="inline-block mb-4"
                    style={{
                      filter: 'blur(3px)',
                      userSelect: 'none',
                      pointerEvents: 'none',
                      opacity: 0.5
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 0.5 } : { opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 0.5, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 + 0.2 }}
                  >
                    <LiquidGlass
                      borderRadius={30}
                      blur={0.6}
                      contrast={1.2}
                      brightness={1.1}
                      saturation={1.2}
                      shadowIntensity={0.25}
                      elasticity={0.6}
                    >
                      <div className="px-8 py-4">
                        <h3 
                          className="text-xl font-semibold"
                          style={{ 
                            color: '#1A1A1A',
                            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                            letterSpacing: '-0.01em'
                          }}
                        >
                          {product.name}
                        </h3>
                      </div>
                    </LiquidGlass>
                  </motion.div>

                  {/* Description - Centered */}
                  <motion.p 
                    className="text-base text-center"
                    style={{ 
                      color: '#666666',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 500,
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                  >
                    {product.description}
                  </motion.p>

                  {/* Status Badge - Absolute Top Right */}
                  <motion.span 
                    className="absolute top-4 right-0 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap"
                    style={{ 
                      background: 'rgba(240, 147, 251, 0.1)',
                      color: '#f093fb',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.1 + 0.4 }}
                    whileHover={{
                      background: divisions[1].gradient,
                      color: '#FFFFFF',
                      scale: 1.05,
                    }}
                  >
                    Coming Soon
                  </motion.span>

                  {/* Subtle divider */}
                  {index < hardwareProducts.length - 1 && (
                    <motion.div
                      className="mt-8 h-px"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.06), transparent)',
                      }}
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    />
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {/* Services Tab - Floating Glass Names */}
          {activeTab === 'services' && (
            <div className="max-w-4xl mx-auto space-y-8">
              {services.map((service, index) => {
                const isActive = service.status === 'active' && service.isActive && !service.isComingSoon
                const isClickable = isActive
                
                return (
                  <motion.div
                    key={service.id}
                    className="group relative"
                    style={{
                      cursor: isClickable ? 'pointer' : 'default'
                    }}
                    initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    onClick={() => isClickable && onServiceClick(service.id)}
                    whileHover={
                      prefersReducedMotion.current
                        ? {}
                        : {
                            y: -6,
                            transition: { duration: 0.4, ease: [0.4, 0.0, 0.2, 1] }
                          }
                    }
                  >
                    {/* Liquid Glass Name Pill */}
                    <motion.div
                      className="inline-block mb-4"
                      style={{
                        opacity: isActive ? 1 : 0.6,
                      }}
                      initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, scale: 0.9 }}
                      animate={{ opacity: isActive ? 1 : 0.6, scale: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.1 + 0.2 }}
                      whileHover={
                        prefersReducedMotion.current || !isClickable
                          ? {}
                          : {
                              scale: 1.03,
                              y: -2,
                              transition: { duration: 0.3 }
                            }
                      }
                    >
                      <LiquidGlass
                        borderRadius={30}
                        blur={0.6}
                        contrast={1.2}
                        brightness={1.1}
                        saturation={1.2}
                        shadowIntensity={0.25}
                        elasticity={0.6}
                      >
                        <div className="px-8 py-4">
                          <h3 
                            className="text-xl font-semibold"
                            style={{ 
                              color: '#1A1A1A',
                              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                              letterSpacing: '-0.01em'
                            }}
                          >
                            {service.name}
                          </h3>
                        </div>
                      </LiquidGlass>
                    </motion.div>

                    {/* Description - Centered */}
                    <motion.p 
                      className="text-base text-center"
                      style={{ 
                        color: '#666666',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 500,
                      }}
                      initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                    >
                      {service.description}
                    </motion.p>

                    {/* Status Badge - Absolute Top Right */}
                    <motion.span 
                      className="absolute top-4 right-0 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap"
                      style={{ 
                        background: isActive 
                          ? 'rgba(46, 204, 113, 0.1)' 
                          : service.isComingSoon 
                            ? 'rgba(79, 172, 254, 0.1)'
                            : 'rgba(255, 193, 7, 0.1)',
                        color: isActive 
                          ? '#2ECC71' 
                          : service.isComingSoon 
                            ? '#4facfe'
                            : '#FFC107',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      }}
                      initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 + 0.4 }}
                      whileHover={
                        isActive ? {
                          background: divisions[2].gradient,
                          color: '#FFFFFF',
                          scale: 1.05,
                        } : {}
                      }
                    >
                      {isActive ? 'Available' : service.isComingSoon ? 'Coming Soon' : 'Maintenance'}
                    </motion.span>

                    {/* Subtle divider */}
                    {index < services.length - 1 && (
                      <motion.div
                        className="mt-8 h-px"
                        style={{
                          background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.06), transparent)',
                        }}
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                      />
                    )}
                  </motion.div>
                )
              })}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  )
}
